// /app/frontend/src/pages/ProgressionPage.jsx
// Page "Ma progression pratique" : 23 étapes de la trame auto-école, cochables
// avec 4 statuts (Non abordée / En cours / Acquise / À revoir), reliées aux
// fiches REMC officielles. Copie séance du jour → texte prêt à envoyer à l'élève.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import { PROGRESSION, STATUTS, TOTAL_ETAPES } from '../data/progressionAutoEcole';
import { COURS_VOITURE_ELEVE } from '../data/coursVoitureEleve';
import { Copy, Check, ArrowLeft, GraduationCap, ClipboardList, RotateCcw, Loader2, Circle, CheckCircle2, Clock3, ArrowRight, Sparkles, ChevronDown, ChevronUp, BookOpen, Target, ShieldAlert, Zap, ListChecks, AlertTriangle, Film, Eye, EyeOff, MessageCircle, X } from 'lucide-react';
import { sparkle, celebrate } from '../lib/celebrate';
import { toTu, toTuFlat } from '../lib/toTu';
import { getStepSvg } from '../data/stepSvgMap';
import StepVisualModal from '../components/StepVisualModal';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode } from 'lucide-react';
import { getPublicOrigin } from '../lib/publicUrl';

const STATUS_KEY = 'cv_progression_status_v1';
const TODAY_KEY  = 'cv_progression_today_v1';

function useStore(key, init) {
  const [v, setV] = useState(() => {
    try {
      const r = localStorage.getItem(key);
      if (!r) return init;
      return JSON.parse(r);
    } catch { return init; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ } }, [key, v]);
  return [v, setV];
}

async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

function findRemcTitre(remcCode) {
  if (!remcCode || remcCode.length < 3) return null;
  const comp = COURS_VOITURE_ELEVE.find(c => c.code === remcCode.slice(0, 2));
  if (!comp) return null;
  const letter = remcCode.slice(2);
  const lecon = comp.lecons.find(l => l.letter === letter);
  return lecon?.titre || null;
}

function findLessonByCode(remcCode) {
  if (!remcCode || remcCode.length < 3) return null;
  const comp = COURS_VOITURE_ELEVE.find(c => c.code === remcCode.slice(0, 2));
  if (!comp) return null;
  const letter = remcCode.slice(2);
  const lecon = comp.lecons.find(l => l.letter === letter);
  if (!lecon) return null;
  return { ...lecon, compCode: comp.code, compTitre: comp.title || comp.titre };
}

function StepRemcPanel({ lesson }) {
  if (!lesson) return null;
  const remc = lesson.remc || {};
  return (
    <div className="mt-3 pt-3 border-t border-white/5 space-y-3" data-testid={`step-remc-panel-${lesson.compCode}${lesson.letter}`}>
      {lesson.objectif && (
        <div>
          <div className="text-sky-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><Target size={10} /> Objectif</div>
          <p className="text-white/85 text-[12.5px] leading-relaxed">{toTu(lesson.objectif)}</p>
        </div>
      )}
      {remc.pourquoi?.intro && (
        <div className="rounded-lg border border-emerald-500/25 bg-emerald-950/15 p-3">
          <div className="text-emerald-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><Sparkles size={10} /> POURQUOI</div>
          <p className="text-white/85 text-[12.5px] leading-relaxed whitespace-pre-wrap">{toTu(remc.pourquoi.intro)}</p>
          {remc.pourquoi.avantages?.length > 0 && (
            <ul className="mt-2 space-y-1 text-white/80 text-[12px]">
              {remc.pourquoi.avantages.slice(0, 4).map((a, i) => <li key={i} className="flex gap-2"><span className="text-emerald-400 shrink-0">✓</span> {toTuFlat(a)}</li>)}
            </ul>
          )}
        </div>
      )}
      {remc.comment && (remc.comment.a_traiter || remc.comment.savoir_faire?.length > 0) && (
        <div className="rounded-lg border border-sky-500/25 bg-sky-950/15 p-3">
          <div className="text-sky-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><Zap size={10} /> COMMENT</div>
          {remc.comment.a_traiter && <p className="text-white/85 text-[12.5px] leading-relaxed mb-2">{toTu(remc.comment.a_traiter)}</p>}
          {remc.comment.savoir_faire?.length > 0 && (
            <ul className="mt-1 space-y-1 text-white/80 text-[12px]">
              {remc.comment.savoir_faire.slice(0, 5).map((a, i) => <li key={i} className="flex gap-2"><span className="text-sky-400 shrink-0">→</span> {toTuFlat(a)}</li>)}
            </ul>
          )}
        </div>
      )}
      {remc.risques && (remc.risques.intro || (Array.isArray(remc.risques.risques_listes) && remc.risques.risques_listes.length > 0)) && (
        <div className="rounded-lg border border-red-500/30 bg-red-950/15 p-3">
          <div className="text-red-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><ShieldAlert size={10} /> RISQUES</div>
          {remc.risques.intro && <p className="text-white/85 text-[12.5px] leading-relaxed mb-2">{toTu(remc.risques.intro)}</p>}
          {Array.isArray(remc.risques.risques_listes) && remc.risques.risques_listes.length > 0 && (
            <ul className="space-y-1 text-white/80 text-[12px]">
              {remc.risques.risques_listes.slice(0, 5).map((r, i) => <li key={i} className="flex gap-2"><span className="text-red-400 shrink-0">⚠</span> {toTuFlat(r)}</li>)}
            </ul>
          )}
        </div>
      )}
      {(lesson.points_cles?.length > 0 || lesson.erreurs_a_eviter?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {lesson.points_cles?.length > 0 && (
            <div className="rounded-lg border border-amber-500/25 bg-amber-950/10 p-3">
              <div className="text-amber-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><ListChecks size={10} /> À mémoriser</div>
              <ul className="space-y-1 text-white/80 text-[12px]">
                {lesson.points_cles.slice(0, 4).map((p, i) => <li key={i} className="flex gap-2"><span className="text-amber-400 shrink-0">★</span> {toTuFlat(p)}</li>)}
              </ul>
            </div>
          )}
          {lesson.erreurs_a_eviter?.length > 0 && (
            <div className="rounded-lg border border-red-500/25 bg-red-950/10 p-3">
              <div className="text-red-300 text-[10px] font-mono-spec uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5"><AlertTriangle size={10} /> Erreurs à éviter</div>
              <ul className="space-y-1 text-white/80 text-[12px]">
                {lesson.erreurs_a_eviter.slice(0, 4).map((p, i) => <li key={i} className="flex gap-2"><span className="text-red-400 shrink-0">✗</span> {toTuFlat(p)}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      <div>
        <Link
          to={`/cours/eleve/voiture?remc=${lesson.compCode}${lesson.letter}`}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 text-[11.5px] font-semibold"
        >
          <BookOpen size={11} /> Fiche REMC complète <ArrowRight size={11} />
        </Link>
      </div>
    </div>
  );
}

function StatusPill({ status, onChange, testid }) {
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      {STATUTS.map((s) => {
        const active = status === s.key;
        const base = 'inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold border transition';
        const styles = {
          todo:     active ? 'bg-white/10 border-white/25 text-white/85'          : 'bg-transparent border-white/10 text-white/50 hover:text-white/80 hover:border-white/25',
          en_cours: active ? 'bg-sky-500/25 border-sky-500/60 text-sky-200'       : 'bg-transparent border-white/10 text-white/50 hover:text-sky-200 hover:border-sky-500/40',
          acquis:   active ? 'bg-emerald-500/25 border-emerald-500/60 text-emerald-200' : 'bg-transparent border-white/10 text-white/50 hover:text-emerald-200 hover:border-emerald-500/40',
          a_revoir: active ? 'bg-amber-500/25 border-amber-500/60 text-amber-200' : 'bg-transparent border-white/10 text-white/50 hover:text-amber-200 hover:border-amber-500/40',
        }[s.key];
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange(s.key)}
            className={`${base} ${styles}`}
            data-testid={`${testid}-${s.key}`}
            aria-pressed={active}
          >
            {s.key === 'acquis' && <CheckCircle2 size={11} />}
            {s.key === 'en_cours' && <Clock3 size={11} />}
            {s.key === 'a_revoir' && <RotateCcw size={11} />}
            {s.key === 'todo' && <Circle size={11} />}
            {s.short}
          </button>
        );
      })}
    </div>
  );
}

function buildSeanceReport(todayIds, statusMap) {
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const acquis = [];
  const enCours = [];
  const aRevoir = [];
  const nonStatue = [];

  PROGRESSION.forEach((phase) => {
    phase.steps.forEach((step) => {
      if (!todayIds.has(step.id)) return;
      const st = statusMap[step.id] || 'todo';
      const item = { ...step, phase: phase.phase };
      if (st === 'acquis') acquis.push(item);
      else if (st === 'en_cours') enCours.push(item);
      else if (st === 'a_revoir') aRevoir.push(item);
      else nonStatue.push(item);
    });
  });

  const total = acquis.length + enCours.length + aRevoir.length + nonStatue.length;
  if (total === 0) return null;

  const lines = [];
  lines.push(`Séance du ${dateStr}`);
  lines.push(`${total} étape${total > 1 ? 's' : ''} travaillée${total > 1 ? 's' : ''}.`);
  lines.push('');

  if (acquis.length > 0) {
    lines.push('Acquises :');
    acquis.forEach((s) => lines.push(`- ${s.id} · ${s.label}`));
    lines.push('');
  }
  if (enCours.length > 0) {
    lines.push("En cours d'acquisition :");
    enCours.forEach((s) => lines.push(`- ${s.id} · ${s.label}`));
    lines.push('');
  }
  if (aRevoir.length > 0) {
    lines.push('À revoir :');
    aRevoir.forEach((s) => lines.push(`- ${s.id} · ${s.label}`));
    lines.push('');
  }
  if (nonStatue.length > 0) {
    lines.push('Autres étapes abordées :');
    nonStatue.forEach((s) => lines.push(`- ${s.id} · ${s.label}`));
    lines.push('');
  }

  // Prochaine séance suggérée
  const toContinue = [...aRevoir, ...enCours];
  if (toContinue.length > 0) {
    lines.push('Prochaine séance, on reprend :');
    toContinue.forEach((s) => lines.push(`- ${s.id} · ${s.label}`));
  }

  return lines.join('\n').trim();
}

// Version DÉTAILLÉE — trame de cours complète à envoyer à l'élève.
// Ajoute Pourquoi + Comment + Risques + À mémoriser + Erreurs pour chaque étape.
// (toTu / toTuFlat sont importés depuis /lib/toTu.js — partagés avec EtapeRemcCard)

function buildSeanceReportDetailed(todayIds, statusMap) {
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const acquis = [];
  const enCours = [];
  const aRevoir = [];
  const nonStatue = [];

  PROGRESSION.forEach((phase) => {
    phase.steps.forEach((step) => {
      if (!todayIds.has(step.id)) return;
      const st = statusMap[step.id] || 'todo';
      const item = { ...step, phase: phase.phase };
      if (st === 'acquis') acquis.push(item);
      else if (st === 'en_cours') enCours.push(item);
      else if (st === 'a_revoir') aRevoir.push(item);
      else nonStatue.push(item);
    });
  });

  const total = acquis.length + enCours.length + aRevoir.length + nonStatue.length;
  if (total === 0) return null;

  const statusLabel = {
    acquis:   'Acquise',
    en_cours: "En cours d'acquisition",
    a_revoir: 'À revoir',
    todo:     'Abordée',
  };

  const cleanBullets = (arr, max = 5) => (Array.isArray(arr) ? arr.slice(0, max) : []);
  const flat = (v) => (typeof v === 'string' ? v : (v?.titre || v?.text || '')).trim();

  const lines = [];
  lines.push('==============================================');
  lines.push(`  SÉANCE DU ${dateStr.toUpperCase()}`);
  lines.push(`  ${total} étape${total > 1 ? 's' : ''} travaillée${total > 1 ? 's' : ''}`);
  lines.push('==============================================');
  lines.push('');

  // On garde l'ordre logique de la progression (M1..P3)
  const ordered = [...acquis, ...enCours, ...aRevoir, ...nonStatue].sort((a, b) => {
    const order = { Mécanique: 0, Observation: 1, 'Préparation permis': 2 };
    const pa = order[a.phase] ?? 9;
    const pb = order[b.phase] ?? 9;
    if (pa !== pb) return pa - pb;
    return a.id.localeCompare(b.id, undefined, { numeric: true });
  });

  ordered.forEach((s, index) => {
    const st = statusMap[s.id] || 'todo';
    const lesson = findLessonByCode(s.remc);
    const remc = lesson?.remc || {};

    // Séparateur étape
    lines.push('');
    lines.push(`━━━ ÉTAPE ${s.id} · ${s.label} ━━━`);
    lines.push(`Phase ${s.phase} · Fiche REMC ${s.remc || '—'} · Statut : ${statusLabel[st]}`);
    lines.push('');

    // Objectif
    if (lesson?.objectif) {
      lines.push('Objectif');
      lines.push(toTu(lesson.objectif));
      lines.push('');
    }

    // POURQUOI
    if (remc.pourquoi?.intro) {
      lines.push('POURQUOI');
      lines.push(toTu(remc.pourquoi.intro));
      if (remc.pourquoi.avantages?.length) {
        lines.push('');
        lines.push('Avantages pour toi :');
        cleanBullets(remc.pourquoi.avantages).forEach((a) => lines.push(`- ${toTu(flat(a))}`));
      }
      if (remc.pourquoi.inconvenients?.length) {
        lines.push('');
        lines.push('Ce que tu risques sans le maîtriser :');
        cleanBullets(remc.pourquoi.inconvenients).forEach((a) => lines.push(`- ${toTu(flat(a))}`));
      }
      lines.push('');
    }

    // COMMENT
    if (remc.comment && (remc.comment.a_traiter || remc.comment.savoir_faire?.length || remc.comment.savoir?.length)) {
      lines.push('COMMENT');
      if (remc.comment.a_traiter) {
        lines.push(toTu(remc.comment.a_traiter));
        lines.push('');
      }
      if (remc.comment.savoir_faire?.length) {
        lines.push('Ce que tu vas savoir-faire :');
        cleanBullets(remc.comment.savoir_faire).forEach((a) => lines.push(`- ${toTu(flat(a))}`));
        lines.push('');
      }
      if (remc.comment.savoir?.length) {
        lines.push('Ce que tu dois savoir (connaissances) :');
        cleanBullets(remc.comment.savoir).forEach((a) => lines.push(`- ${toTu(flat(a))}`));
        lines.push('');
      }
    }

    // RISQUES
    if (remc.risques && (remc.risques.intro || (Array.isArray(remc.risques.risques_listes) && remc.risques.risques_listes.length))) {
      lines.push('RISQUES');
      if (remc.risques.intro) {
        lines.push(toTu(remc.risques.intro));
        lines.push('');
      }
      if (Array.isArray(remc.risques.risques_listes) && remc.risques.risques_listes.length) {
        lines.push('Risques à connaître :');
        cleanBullets(remc.risques.risques_listes).forEach((a) => lines.push(`- ${toTu(flat(a))}`));
        lines.push('');
      }
    }

    // À mémoriser
    if (lesson?.points_cles?.length) {
      lines.push('À mémoriser');
      cleanBullets(lesson.points_cles).forEach((a) => lines.push(`- ${toTu(flat(a))}`));
      lines.push('');
    }

    // Erreurs à éviter
    if (lesson?.erreurs_a_eviter?.length) {
      lines.push('Erreurs à éviter');
      cleanBullets(lesson.erreurs_a_eviter).forEach((a) => lines.push(`- ${toTu(flat(a))}`));
      lines.push('');
    }

    if (index < ordered.length - 1) {
      lines.push('');
    }
  });

  // Récapitulatif final
  lines.push('');
  lines.push('----------------------------------------------');
  lines.push('RÉCAPITULATIF');
  lines.push('----------------------------------------------');
  if (acquis.length)   lines.push(`Acquises (${acquis.length}) : ` + acquis.map((s) => s.id).join(', '));
  if (enCours.length)  lines.push(`En cours (${enCours.length}) : ` + enCours.map((s) => s.id).join(', '));
  if (aRevoir.length)  lines.push(`À revoir (${aRevoir.length}) : ` + aRevoir.map((s) => s.id).join(', '));
  if (nonStatue.length) lines.push(`Abordées (${nonStatue.length}) : ` + nonStatue.map((s) => s.id).join(', '));

  const toContinue = [...aRevoir, ...enCours];
  if (toContinue.length) {
    lines.push('');
    lines.push('Prochaine séance, on reprend :');
    toContinue.forEach((s) => lines.push(`- ${s.id} · ${s.label}`));
  }

  return lines.join('\n').trim();
}

export default function ProgressionPage() {
  const { user } = useAuth();
  const [status, setStatus] = useStore(STATUS_KEY, {});
  const [todayRaw, setTodayRaw] = useStore(TODAY_KEY, []);
  const todayIds = useMemo(() => new Set(todayRaw), [todayRaw]);

  // Modal visuel pédagogique
  const [visualStep, setVisualStep] = useState(null); // step objet ou null

  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copiedFull, setCopiedFull] = useState(false);
  const [copyingFull, setCopyingFull] = useState(false);
  const [previewMode, setPreviewMode] = useState(null); // null | 'court' | 'detaille'
  const [previewCopied, setPreviewCopied] = useState(false);
  const [openRemc, setOpenRemc] = useState(new Set());
  const toggleRemc = useCallback((id) => {
    setOpenRemc((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const setStepStatus = useCallback((id, st) => {
    setStatus((prev) => ({ ...prev, [id]: st }));
    // ajoute automatiquement à "aujourd'hui" si on statue et ce n'est pas déjà le cas
    if (st && st !== 'todo') {
      setTodayRaw((prev) => (prev.includes(id) ? prev : [...prev, id]));
      // 🎉 acquisition = gros feu d'artifice, autres statuts = sparkle discret
      if (st === 'acquis') celebrate();
      else sparkle();
    }
  }, [setStatus, setTodayRaw]);

  const toggleToday = useCallback((id) => {
    setTodayRaw((prev) => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, [setTodayRaw]);

  const clearToday = () => { setTodayRaw([]); };
  const resetAllStatus = () => {
    if (window.confirm('Réinitialiser TOUS les statuts de progression ? Cette action est irréversible.')) {
      setStatus({});
      setTodayRaw([]);
    }
  };

  // Stats globales
  const stats = useMemo(() => {
    let acquis = 0, enCours = 0, aRevoir = 0;
    PROGRESSION.forEach((p) => p.steps.forEach((s) => {
      const st = status[s.id];
      if (st === 'acquis') acquis++;
      else if (st === 'en_cours') enCours++;
      else if (st === 'a_revoir') aRevoir++;
    }));
    return { acquis, enCours, aRevoir, total: TOTAL_ETAPES };
  }, [status]);

  const percentAcquis = Math.round((stats.acquis / stats.total) * 100);

  // Prochain cours = première étape non "acquis" dans l'ordre de la trame.
  // Priorité : à revoir > en cours > todo (une étape à revoir passe devant une simple todo).
  const prochainCours = useMemo(() => {
    const flat = [];
    PROGRESSION.forEach((phase) => {
      phase.steps.forEach((step, i) => {
        flat.push({ ...step, phase: phase.phase, phaseSlug: phase.slug, phaseColor: phase.color, phaseIdx: i });
      });
    });
    const findByStatus = (target) => flat.find((s) => (status[s.id] || 'todo') === target);
    // Order: a_revoir → en_cours → todo (les acquises sont exclues)
    return findByStatus('a_revoir') || findByStatus('en_cours') || findByStatus('todo') || null;
  }, [status]);

  const onCopySeance = async () => {
    if (copying) return;
    setCopying(true);
    const txt = buildSeanceReport(todayIds, status);
    if (!txt) { setCopying(false); return; }
    const ok = await copyText(txt);
    if (ok) {
      setCopied(true);
      celebrate();
      setTimeout(() => setCopied(false), 2200);
    }
    setCopying(false);
  };

  const onCopySeanceDetaillee = async () => {
    if (copyingFull) return;
    setCopyingFull(true);
    const txt = buildSeanceReportDetailed(todayIds, status);
    if (!txt) { setCopyingFull(false); return; }
    const ok = await copyText(txt);
    if (ok) {
      setCopiedFull(true);
      celebrate();
      setTimeout(() => setCopiedFull(false), 2600);
    }
    setCopyingFull(false);
  };

  const hasTodayItems = todayIds.size > 0;

  // ═══ FLASH SÉANCE — QR partageable de la séance du jour (enseignants uniquement)
  const isTeacher = user && user.role === 'enseignant';
  const [flashLoading, setFlashLoading] = useState(false);
  const [flashSeance, setFlashSeance] = useState(null); // { token, expires_at, ... }
  const [flashCopied, setFlashCopied] = useState(false);
  const [flashError, setFlashError] = useState('');
  // Panneau replié (utile mobile) : par défaut ouvert, replié après génération QR
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  const generateFlashQr = async () => {
    if (!hasTodayItems) return;
    setFlashError('');
    setFlashLoading(true);
    try {
      // Chaque étape « aujourd'hui » avec son statut : acquis / a_revoir / vu (fallback en_cours ou todo → 'vu')
      const stepsArr = Array.from(todayIds).map((id) => {
        const st = status[id];
        const mapped = st === 'acquis' ? 'acquis' : st === 'a_revoir' ? 'a_revoir' : 'vu';
        return { id, status: mapped };
      });
      const res = await api.flashSeanceCreate({
        steps: stepsArr,
        notes: '',
        duration_days: 30,
      });
      setFlashSeance(res);
    } catch (e) {
      setFlashError(e?.response?.data?.detail || 'Erreur lors de la génération.');
    } finally {
      setFlashLoading(false);
    }
  };

  const flashUrl = flashSeance ? `${getPublicOrigin()}/s/${flashSeance.token}` : '';

  const copyFlashUrl = async () => {
    if (!flashUrl) return;
    try {
      await navigator.clipboard.writeText(flashUrl);
      setFlashCopied(true);
      setTimeout(() => setFlashCopied(false), 2200);
    } catch (_) { /* silent */ }
  };

  // Régénère automatiquement quand la sélection du jour change
  useEffect(() => { setFlashSeance(null); setFlashError(''); }, [todayRaw]);

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <Link to="/cours/eleve/voiture" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[13px] font-mono-spec mb-6" data-testid="progression-back">
          <ArrowLeft size={14} /> Retour au cours voiture (REMC)
        </Link>

        <div className="mb-8">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-bold font-mono-spec mb-2 flex items-center gap-2">
            <GraduationCap size={12} /> {isTeacher ? 'Progression pratique · auto-école' : 'Ma progression pratique'}
          </div>
          <h1 className="font-serif-editorial text-white text-[32px] sm:text-[42px] lg:text-[52px] leading-[1.05] tracking-tight">
            {isTeacher ? 'Ma trame de cours' : 'Ma progression'}
          </h1>
          <p className="text-white/70 text-[14px] sm:text-[15px] max-w-2xl mt-3 leading-relaxed">
            {isTeacher
              ? <>23 étapes concrètes réparties en 3 phases (Mécanique · Observation · Préparation permis). Chaque étape est reliée à la fiche REMC officielle correspondante. Statue l&apos;acquisition en fin de séance, coche « Aujourd&apos;hui » pour préparer un compte-rendu à envoyer à ton élève.</>
              : <>23 étapes concrètes réparties en 3 phases (Mécanique · Observation · Préparation permis). Chaque étape est reliée à la fiche REMC officielle. À la fin de chaque leçon, mets à jour ton statut (Acquis · En cours · À revoir) pour voir ta progression décoller 🚀</>
            }
          </p>
        </div>

        {/* Statistiques globales */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 mb-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-white/60 text-[11px] uppercase tracking-[0.2em] font-mono-spec">Progression globale</div>
              <div className="text-white font-serif-editorial text-[26px] mt-1">{stats.acquis}<span className="text-white/40 text-[18px]">/{stats.total}</span> étapes acquises</div>
            </div>
            <div className="text-right">
              <div className="text-emerald-300 text-[32px] font-bold font-mono-spec">{percentAcquis}%</div>
            </div>
          </div>
          <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all" style={{ width: `${percentAcquis}%` }} />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px]">
            <span className="text-emerald-300"><CheckCircle2 size={11} className="inline mr-1" />{stats.acquis} acquises</span>
            <span className="text-sky-300"><Clock3 size={11} className="inline mr-1" />{stats.enCours} en cours</span>
            <span className="text-amber-300"><RotateCcw size={11} className="inline mr-1" />{stats.aRevoir} à revoir</span>
            <button type="button" onClick={resetAllStatus} className="ml-auto text-white/40 hover:text-red-300 text-[11.5px] underline underline-offset-2" data-testid="reset-progression">Tout réinitialiser</button>
          </div>
        </div>

        {/* Prochain cours suggéré (première étape non acquise) */}
        {prochainCours && (
          <div className="rounded-2xl border border-sky-500/40 bg-gradient-to-br from-sky-950/40 via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-6 mb-8" data-testid="prochain-cours-card">
            <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-sky-500/20 border border-sky-500/40 flex items-center justify-center text-sky-300">
                <Sparkles size={22} />
              </div>
              <div className="flex-1 min-w-0 basis-[calc(100%-4rem)] sm:basis-auto">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-400 font-bold font-mono-spec">Prochain cours suggéré</div>
                <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight mt-1 break-words">
                  Étape {prochainCours.id} · {prochainCours.label}
                </h3>
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11.5px] font-mono-spec text-white/60">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 bg-white/5">Phase {prochainCours.phase}</span>
                  {prochainCours.remc && (
                    <span className="inline-block px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/40 text-sky-200 text-[10px] font-bold">REMC {prochainCours.remc}</span>
                  )}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
                    (status[prochainCours.id] || 'todo') === 'a_revoir' ? 'border-amber-500/40 bg-amber-500/15 text-amber-300' :
                    (status[prochainCours.id] || 'todo') === 'en_cours' ? 'border-sky-500/40 bg-sky-500/15 text-sky-300' :
                    'border-white/10 bg-white/5 text-white/60'
                  }`}>
                    {(status[prochainCours.id] || 'todo') === 'a_revoir' && <><RotateCcw size={10} /> À revoir en priorité</>}
                    {(status[prochainCours.id] || 'todo') === 'en_cours' && <><Clock3 size={10} /> Reprendre en cours</>}
                    {(status[prochainCours.id] || 'todo') === 'todo' && <><Circle size={10} /> Nouvelle étape</>}
                  </span>
                </div>
              </div>
              {prochainCours.remc && (
                <Link
                  to={`/cours/eleve/voiture?remc=${prochainCours.remc}`}
                  data-testid="prochain-cours-open"
                  className="h-10 px-4 rounded-full bg-sky-500 hover:bg-sky-400 text-sky-950 text-[13px] font-bold inline-flex items-center justify-center gap-1.5 shrink-0 w-full sm:w-auto mt-3 sm:mt-0"
                >
                  Ouvrir la fiche REMC <ArrowRight size={13} />
                </Link>
              )}
            </div>
            {/* Action : marquer terminé + passer à la suivante */}
            <div className="mt-4 pt-4 border-t border-sky-500/20 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => { setStepStatus(prochainCours.id, 'acquis'); }}
                data-testid="prochain-cours-mark-done"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[13px] font-bold transition shadow-lg shadow-emerald-950/40 w-full sm:w-auto justify-center"
              >
                <CheckCircle2 size={14} /> Marquer terminé & passer à la suivante
              </button>
              <span className="text-white/50 text-[11.5px] font-mono-spec">La carte se met à jour automatiquement vers l&apos;étape non acquise suivante.</span>
            </div>
          </div>
        )}

        {/* Panneau séance du jour — sticky desktop only, repliable sur mobile pour libérer l'écran */}
        <div className={`sm:sticky sm:top-3 z-20 rounded-xl border p-4 mb-8 transition ${hasTodayItems ? 'border-emerald-500/40 bg-emerald-950/25 shadow-xl shadow-emerald-950/40' : 'border-white/10 bg-[#0d0d0e]/95 backdrop-blur'}`}>
          {/* Header : titre + toggle repli */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.2em] font-mono-spec text-emerald-300 font-bold">{isTeacher ? 'Séance du jour' : 'Ma séance du jour'}</div>
              <div className="text-white/85 text-[13.5px] mt-0.5">
                {hasTodayItems
                  ? `${todayIds.size} étape${todayIds.size > 1 ? 's' : ''} sélectionnée${todayIds.size > 1 ? 's' : ''}`
                  : isTeacher
                    ? "Coche « Aujourd'hui » sur les étapes que tu as travaillées avec l'élève."
                    : "Coche « Aujourd'hui » sur les étapes vues pendant ta leçon pour garder un souvenir de ta séance."}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPanelCollapsed((v) => !v)}
              data-testid="toggle-seance-panel"
              aria-expanded={!panelCollapsed}
              className="shrink-0 w-9 h-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white inline-flex items-center justify-center transition"
              title={panelCollapsed ? 'Développer' : 'Réduire'}
            >
              {panelCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>

          {/* Contenu détaillé (masqué si replié) */}
          {!panelCollapsed && (
            <div className="max-h-[55vh] sm:max-h-none overflow-y-auto sm:overflow-visible">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={clearToday} disabled={!hasTodayItems} className="h-9 px-3 rounded-full border border-white/10 text-white/60 text-[12px] disabled:opacity-40 hover:text-white hover:border-white/30" data-testid="clear-today">Vider</button>
            <button
              type="button"
              onClick={onCopySeance}
              disabled={!hasTodayItems || copying}
              data-testid="copy-seance"
              title={isTeacher ? "Résumé court (SMS / WhatsApp élève)" : "Résumé court à partager (SMS / WhatsApp)"}
              className={`h-10 px-4 rounded-full text-[12.5px] font-bold inline-flex items-center gap-2 transition ${copied ? 'bg-emerald-500 text-emerald-950' : 'bg-white/10 text-white hover:bg-white/15 border border-white/15'} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {copying ? <Loader2 size={13} className="animate-spin" /> : copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copié !' : 'Résumé court'}
            </button>
            <button
              type="button"
              onClick={onCopySeanceDetaillee}
              disabled={!hasTodayItems || copyingFull}
              data-testid="copy-seance-detaillee"
              title={isTeacher ? "Trame complète avec Pourquoi / Comment / Risques REMC — à envoyer à ton élève" : "Fiche complète (à garder dans tes notes)"}
              className={`h-10 px-4 rounded-full text-[13px] font-bold inline-flex items-center gap-2 transition ${copiedFull ? 'bg-emerald-500 text-emerald-950' : 'bg-white text-black hover:bg-white/90'} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {copyingFull ? <Loader2 size={14} className="animate-spin" /> : copiedFull ? <Check size={14} /> : <ClipboardList size={14} />}
              {copiedFull ? 'Trame copiée !' : (isTeacher ? 'Trame détaillée (REMC)' : 'Fiche complète')}
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode((m) => (m ? null : 'court'))}
              disabled={!hasTodayItems}
              data-testid="preview-seance-message"
              title="Voir le message exactement comme il apparaîtra sur WhatsApp / SMS avant de le copier"
              className={`h-10 px-4 rounded-full text-[12.5px] font-bold inline-flex items-center gap-2 transition ${previewMode ? 'bg-emerald-600 text-white border border-emerald-500' : 'bg-white/10 text-white hover:bg-white/15 border border-white/15'} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {previewMode ? <EyeOff size={13} /> : <Eye size={13} />}
              {previewMode ? "Masquer l'aperçu" : 'Aperçu du message'}
            </button>
          </div>

          {/* ═══ APERÇU STYLE WHATSAPP — le message tel qu'il sera reçu */}
          {previewMode && hasTodayItems && (() => {
            const previewTxt = previewMode === 'court' ? buildSeanceReport(todayIds, status) : buildSeanceReportDetailed(todayIds, status);
            const now = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
            const copyPreview = async () => {
              const ok = await copyText(previewTxt || '');
              if (ok) { setPreviewCopied(true); setTimeout(() => setPreviewCopied(false), 2200); }
            };
            return (
              <div className="mt-4 rounded-2xl overflow-hidden border border-white/10" data-testid="seance-preview-panel">
                {/* Header façon conversation */}
                <div className="flex items-center gap-3 px-4 py-2.5 bg-[#1f2c34]">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white shrink-0">
                    <GraduationCap size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-[13px] font-semibold leading-tight">{isTeacher ? 'Ton élève' : 'Moi'}</div>
                    <div className="text-white/45 text-[10.5px]">aperçu du message avant envoi</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setPreviewMode('court')}
                      data-testid="preview-mode-court"
                      className={`h-7 px-2.5 rounded-full text-[10.5px] font-bold transition ${previewMode === 'court' ? 'bg-emerald-600 text-white' : 'bg-white/10 text-white/60 hover:text-white'}`}
                    >Court</button>
                    <button
                      type="button"
                      onClick={() => setPreviewMode('detaille')}
                      data-testid="preview-mode-detaille"
                      className={`h-7 px-2.5 rounded-full text-[10.5px] font-bold transition ${previewMode === 'detaille' ? 'bg-emerald-600 text-white' : 'bg-white/10 text-white/60 hover:text-white'}`}
                    >Détaillé</button>
                    <button type="button" onClick={() => setPreviewMode(null)} data-testid="preview-close" className="w-7 h-7 rounded-full text-white/50 hover:text-white hover:bg-white/10 inline-flex items-center justify-center">
                      <X size={13} />
                    </button>
                  </div>
                </div>
                {/* Fond de chat + bulle verte */}
                <div className="px-3 sm:px-5 py-4 max-h-[46vh] overflow-y-auto" style={{ background: '#0b141a', backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '18px 18px' }}>
                  <div className="flex justify-end">
                    <div className="relative max-w-[92%] sm:max-w-[80%] rounded-xl rounded-tr-sm px-3 py-2 shadow-md" style={{ background: '#005c4b' }} data-testid="preview-bubble">
                      <pre className="whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-white/95 m-0">{previewTxt}</pre>
                      <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-white/55">
                        {now}
                        <Check size={12} className="text-sky-300 -mr-2" /><Check size={12} className="text-sky-300" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Barre d'action */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1f2c34]">
                  <span className="flex-1 text-white/45 text-[11px] leading-snug hidden sm:block">Vérifie le contenu, puis copie-le pour le coller dans WhatsApp ou SMS.</span>
                  <button
                    type="button"
                    onClick={copyPreview}
                    data-testid="preview-copy-btn"
                    className={`h-9 px-4 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 transition ${previewCopied ? 'bg-emerald-500 text-emerald-950' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}
                  >
                    {previewCopied ? <><Check size={12} /> Copié !</> : <><MessageCircle size={12} /> Copier ce message</>}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ═══ QR CODE PARTAGEABLE ÉLÈVE — enseignants uniquement */}
          {isTeacher && (
            <div className="mt-4 pt-4 border-t border-white/10" data-testid="flash-qr-section">
              {!flashSeance ? (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-full sm:min-w-0 basis-full sm:basis-0">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] font-mono-spec text-red-300 font-bold flex items-center gap-1.5"><QrCode size={11} /> QR élève sans inscription</div>
                    <div className="text-white/70 text-[12.5px] mt-0.5 leading-snug">
                      Génère un QR code à montrer à ton élève en fin de séance : il verra le récap des compétences vues (🟢 acquis · 🔵 vu · 🟠 à revoir) + un mini-QCM, sans avoir à créer de compte.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={generateFlashQr}
                    disabled={!hasTodayItems || flashLoading}
                    data-testid="flash-generate-qr"
                    className="h-10 px-4 rounded-full text-[12.5px] font-bold inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {flashLoading ? <Loader2 size={14} className="animate-spin" /> : <QrCode size={14} />}
                    {flashLoading ? 'Génération…' : 'Générer le QR de la séance'}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row items-start gap-5">
                  <div className="bg-white p-3 rounded-xl shadow-lg shrink-0" data-testid="flash-qr">
                    <QRCodeSVG value={flashUrl} size={140} level="H" includeMargin={false} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] font-mono-spec text-emerald-300 font-bold flex items-center gap-1.5"><Check size={11} /> QR prêt · valable 30 jours</div>
                    <div className="text-white text-[14px] font-serif-editorial mt-1">L&apos;élève scanne, il voit le récap de sa séance</div>
                    <div className="mt-2 flex gap-1.5">
                      <input
                        readOnly
                        value={flashUrl}
                        data-testid="flash-url"
                        onClick={(e) => e.target.select()}
                        className="flex-1 min-w-0 h-9 rounded-lg bg-white/[0.04] border border-white/10 outline-none px-2.5 text-white text-[12px] font-mono-spec"
                      />
                      <button
                        type="button"
                        onClick={copyFlashUrl}
                        data-testid="flash-copy"
                        className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/15 text-white text-[11.5px] font-semibold inline-flex items-center gap-1.5"
                      >
                        {flashCopied ? <><Check size={12} className="text-emerald-400" /> Copié</> : <><Copy size={12} /> Copier</>}
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                      <a href={flashUrl} target="_blank" rel="noreferrer" data-testid="flash-preview" className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 font-semibold">
                        Aperçu élève <ArrowRight size={11} />
                      </a>
                      <span className="text-white/30">·</span>
                      <button type="button" onClick={generateFlashQr} disabled={flashLoading} className="text-white/50 hover:text-white text-[11px] font-semibold underline">
                        {flashLoading ? 'Régénération…' : 'Régénérer'}
                      </button>
                      <span className="text-white/30">·</span>
                      <span className="text-white/40 font-mono-spec">{flashSeance.steps.length} compétence{flashSeance.steps.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              )}
              {flashError && <div className="mt-2 text-red-400 text-[12px]">{flashError}</div>}
            </div>
          )}
            </div>
          )}
        </div>

        {/* Phases */}
        {PROGRESSION.map((phase) => {
          const acquisPhase = phase.steps.filter((s) => status[s.id] === 'acquis').length;
          return (
            <section key={phase.slug} className="mb-10" data-testid={`phase-${phase.slug}`}>
              <div className={`rounded-xl border p-4 sm:p-5 mb-4 ${phase.color === 'red' ? 'border-red-700/30 bg-red-950/15' : phase.color === 'amber' ? 'border-amber-700/30 bg-amber-950/15' : 'border-emerald-700/30 bg-emerald-950/15'}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-[10.5px] uppercase tracking-[0.2em] font-mono-spec text-white/60 font-bold">Phase</div>
                  <h2 className="font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight">{phase.phase}</h2>
                  <span className="ml-auto text-[12px] text-white/60 font-mono-spec">{acquisPhase}/{phase.steps.length} acquises</span>
                </div>
                <p className="text-white/65 text-[13px] mt-1.5 leading-relaxed">{phase.intro}</p>
              </div>

              <div className="space-y-2">
                {phase.steps.map((step, i) => {
                  const remcTitre = findRemcTitre(step.remc);
                  const isToday = todayIds.has(step.id);
                  const isNext = prochainCours?.id === step.id;
                  const svgUrl = getStepSvg(step.id);
                  return (
                    <div
                      key={step.id}
                      className={`rounded-xl border p-3.5 transition ${isNext ? 'border-sky-500/60 bg-sky-950/25 ring-1 ring-sky-500/30' : isToday ? 'border-emerald-500/50 bg-emerald-950/20' : 'border-white/10 bg-[#0d0d0e] hover:border-white/20'}`}
                      data-testid={`step-${step.id}`}
                    >
                      <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                        <div className={`shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center font-mono-spec text-[11px] font-bold ${isNext ? 'bg-sky-500/25 border-sky-500/60 text-sky-200' : 'bg-white/5 border-white/10 text-white/60'}`}>{step.id}</div>
                        <div className="flex-1 min-w-0 basis-[calc(100%-6rem)] sm:basis-auto">
                          <div className="text-white font-serif-editorial text-[15.5px] leading-tight break-words flex items-center gap-2 flex-wrap">
                            <span>{step.label}</span>
                            {isNext && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/25 border border-sky-500/60 text-sky-200 text-[9.5px] font-mono-spec uppercase tracking-wider font-bold">
                                <Sparkles size={9} /> Prochain
                              </span>
                            )}
                          </div>
                          {step.remc && (
                            <Link
                              to={`/cours/eleve/voiture?remc=${step.remc}`}
                              className="mt-1 inline-flex flex-wrap items-center gap-1 text-sky-300/90 hover:text-sky-200 text-[11.5px] font-mono-spec max-w-full"
                              data-testid={`step-remc-${step.id}`}
                              title={`Ouvrir la fiche REMC ${step.remc}`}
                            >
                              <span className="inline-block px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/40 text-sky-200 text-[10px] font-bold whitespace-nowrap">REMC {step.remc}</span>
                              {remcTitre && <span className="truncate max-w-full sm:max-w-none">{remcTitre}</span>}
                            </Link>
                          )}
                        </div>
                        <label className="shrink-0 inline-flex items-center gap-1.5 text-white/70 text-[11.5px] font-semibold cursor-pointer select-none w-full sm:w-auto justify-end sm:ml-auto mt-1 sm:mt-0">
                          <input
                            type="checkbox"
                            checked={isToday}
                            onChange={() => toggleToday(step.id)}
                            className="h-4 w-4 accent-emerald-500"
                            data-testid={`today-${step.id}`}
                          />
                          Aujourd&apos;hui
                        </label>
                      </div>
                      <div className="mt-3 pl-0 sm:pl-12">
                        <StatusPill
                          status={status[step.id] || 'todo'}
                          onChange={(v) => setStepStatus(step.id, v)}
                          testid={`status-${step.id}`}
                        />
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {svgUrl && (
                            <button
                              type="button"
                              onClick={() => setVisualStep({ ...step, phase: phase.phase, phaseSlug: phase.slug })}
                              data-testid={`open-visual-${step.id}`}
                              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-sky-500/50 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 text-[11.5px] font-bold transition"
                              title="Ouvrir le visuel interactif pour animer le cours et valider en un clic"
                            >
                              <Film size={12} /> Visuel interactif
                            </button>
                          )}
                          {step.remc && (
                            <button
                              type="button"
                              onClick={() => toggleRemc(step.id)}
                              data-testid={`toggle-remc-${step.id}`}
                              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-white/15 text-white/60 hover:text-white hover:bg-white/5 text-[11.5px] font-semibold"
                            >
                              <ChevronDown size={12} className={`transition-transform ${openRemc.has(step.id) ? 'rotate-180' : ''}`} />
                              {openRemc.has(step.id) ? 'Masquer' : 'Voir'} le REMC
                            </button>
                          )}
                        </div>
                        {openRemc.has(step.id) && step.remc && (
                          <StepRemcPanel lesson={findLessonByCode(step.remc)} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        <div className="mt-10 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-white/55 text-[12.5px]">
          <ClipboardList size={13} className="inline mr-1.5 text-white/40" />
          Les statuts et la sélection « Aujourd&apos;hui » sont enregistrés localement dans ton navigateur (aucune donnée n&apos;est envoyée au serveur).
        </div>
      </div>

      {/* Modal visuel pédagogique interactif */}
      <StepVisualModal
        open={!!visualStep}
        onClose={() => setVisualStep(null)}
        step={visualStep}
        svgUrl={visualStep ? getStepSvg(visualStep.id) : null}
        currentStatus={visualStep ? (status[visualStep.id] || 'todo') : null}
        onStatus={(st) => visualStep && setStepStatus(visualStep.id, st)}
      />
    </PageShell>
  );
}
